/**
 * @file Showcase 基础控件交互 Demo
 * @description Dialog / SearchInput / Select / GradeSelect / Radio / Switch 等本地演示状态。
 *
 * @whenToUse 仅由 `src/pages/showcase-page.tsx` 引用。
 * @whenNotToUse 业务页面；勿当通用表单示例导出到其他路由。
 */

import { useState } from 'react'
import {
  EducationStage,
  educationStageMap,
} from '@/enum/common.ts'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { GradeSelect } from '@/components/ui/grade-select.tsx'
import { Radio, RadioGroup } from '@/components/ui/radio.tsx'
import { SearchInput } from '@/components/ui/search-input.tsx'
import { Select } from '@/components/ui/select.tsx'
import { Switch } from '@/components/ui/switch.tsx'

/** Dialog 打开示例 */
export function DialogDemo() {
  const [open, setOpen] = useState(false)

  return (
    <>
      <Button onClick={() => setOpen(true)}>打开 Dialog</Button>
      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="示例对话框"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button onClick={() => setOpen(false)}>确认</Button>
          </>
        }
      >
        <p className="text-sm text-muted">
          这是 Dialog 内容区。长内容会走 ScrollArea，滚动条悬浮不占宽。
        </p>
        <div className="mt-3 space-y-2">
          {Array.from({ length: 8 }, (_, i) => (
            <p key={i} className="text-sm">
              示例段落 {i + 1}：用于验证弹层内滚动与 fade。
            </p>
          ))}
        </div>
      </Dialog>
    </>
  )
}

/** SearchInput 受控示例 */
export function SearchInputDemo() {
  const [keyword, setKeyword] = useState('Promot')
  return (
    <div className="max-w-md">
      <SearchInput
        value={keyword}
        placeholder="搜索…"
        onChange={(event) => setKeyword(event.target.value)}
        onClear={() => setKeyword('')}
      />
    </div>
  )
}

/** Select 受控示例（含可滚动长列表） */
export function SelectDemo() {
  const [city, setCity] = useState('sh')
  const options = [
    { value: 'bj', label: '北京' },
    { value: 'sh', label: '上海' },
    { value: 'gz', label: '广州' },
    { value: 'sz', label: '深圳' },
    ...Array.from({ length: 12 }, (_, i) => ({
      value: `c-${i}`,
      label: `更多城市 ${i + 1}`,
    })),
  ]
  return (
    <div className="max-w-md">
      <Select
        value={city}
        onChange={setCity}
        placeholder="选择城市"
        options={options}
      />
    </div>
  )
}

/** GradeSelect：按教育阶段过滤年级 */
export function GradeSelectDemo() {
  const [stage, setStage] = useState<string>(EducationStage.JUNIOR_HIGH)
  const [grade, setGrade] = useState('')
  const stageOptions = [
    { value: '', label: '全部阶段' },
    ...Object.entries(educationStageMap).map(([value, label]) => ({
      value,
      label,
    })),
  ]

  return (
    <div className="grid max-w-md gap-3 sm:grid-cols-2">
      <FormField label="教育阶段">
        <Select
          value={stage}
          options={stageOptions}
          placeholder="全部阶段"
          onChange={setStage}
        />
      </FormField>
      <FormField label="年级">
        <GradeSelect
          value={grade}
          stage={stage}
          allowEmpty
          emptyLabel="请选择"
          onChange={setGrade}
        />
      </FormField>
    </div>
  )
}

/** RadioGroup 受控示例 */
export function RadioDemo() {
  const [status, setStatus] = useState('active')
  return (
    <RadioGroup value={status} onChange={setStatus}>
      <Radio value="active" label="启用" />
      <Radio value="inactive" label="停用" />
      <Radio value="draft" label="草稿" />
    </RadioGroup>
  )
}

/** Switch 受控示例 */
export function SwitchDemo() {
  const [on, setOn] = useState(true)
  return (
    <div className="flex items-center gap-3">
      <Switch checked={on} onCheckedChange={setOn} aria-label="启用通知" />
      <span className="text-sm text-muted">{on ? '已开启' : '已关闭'}</span>
    </div>
  )
}
